/**
 * RangoFechas.jsx  v2
 * Selector de rango de fechas reutilizable
 * Usa formato YYYY-MM-DDTHH:mm:ss (sin espacios) para compatibilidad con NPM
 *
 * Props:
 *   value     {object}  { from, to }  strings 'YYYY-MM-DDTHH:mm:ss'
 *   onChange  {fn}      callback({ from, to })
 *   size      {string}  'small' | 'medium' (default 'small')
 */

import { useState } from 'react';
import {
    Box, Button, ButtonGroup, Collapse, Stack, Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

// ── Helpers — formato con T como separador, nunca espacios ───────────────────
export function fmtFrom(d) {
    return dayjs(d).format('YYYY-MM-DDT00:00:00');
}

export function fmtTo(d) {
    return dayjs(d).format('YYYY-MM-DDT23:59:59');
}

export function mesActual() {
    return {
        from: dayjs().startOf('month').format('YYYY-MM-DDT00:00:00'),
        to: dayjs().format('YYYY-MM-DDTHH:mm:ss'),
    };
}

export function rangoRapido(tipo) {
    const hoy = dayjs();
    switch (tipo) {
        case 'hoy': return { from: hoy.format('YYYY-MM-DDT00:00:00'), to: hoy.format('YYYY-MM-DDTHH:mm:ss') };
        case '7d': return { from: hoy.subtract(7, 'day').format('YYYY-MM-DDT00:00:00'), to: hoy.format('YYYY-MM-DDTHH:mm:ss') };
        case '30d': return { from: hoy.subtract(30, 'day').format('YYYY-MM-DDT00:00:00'), to: hoy.format('YYYY-MM-DDTHH:mm:ss') };
        case 'mes': return mesActual();
        default: return mesActual();
    }
}

const RANGOS_RAPIDOS = [
    { val: 'hoy', label: 'Hoy' },
    { val: '7d', label: '7 días' },
    { val: '30d', label: '30 días' },
    { val: 'mes', label: 'Este mes' },
];

export default function RangoFechas({ value, onChange, size = 'small' }) {
    const [modo, setModo] = useState('mes');
    const [desdeTmp, setDesdeTmp] = useState(dayjs().startOf('month'));
    const [hastaTmp, setHastaTmp] = useState(dayjs());
    const [showCustom, setShowCustom] = useState(false);

    function aplicarRapido(tipo) {
        setModo(tipo);
        setShowCustom(false);
        onChange(rangoRapido(tipo));
    }

    function aplicarCustom() {
        if (!desdeTmp || !hastaTmp) return;
        if (desdeTmp.isAfter(hastaTmp)) return;
        onChange({
            from: fmtFrom(desdeTmp),
            to: fmtTo(hastaTmp),
        });
        setModo('custom');
        setShowCustom(false);
    }

    // Formatear para mostrar al usuario (legible)
    function fmtDisplay(isoStr) {
        if (!isoStr) return '';
        return dayjs(isoStr).format('DD/MM/YY HH:mm');
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <Stack spacing={1}>
                {/* Botones rápidos + personalizado */}
                <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" gap={0.5}>
                    <ButtonGroup size={size} variant="outlined">
                        {RANGOS_RAPIDOS.map((r) => (
                            <Button
                                key={r.val}
                                onClick={() => aplicarRapido(r.val)}
                                variant={modo === r.val ? 'contained' : 'outlined'}
                                sx={{ fontSize: 11, px: 1.2 }}
                            >
                                {r.label}
                            </Button>
                        ))}
                    </ButtonGroup>
                    <Button
                        size={size}
                        variant={modo === 'custom' ? 'contained' : 'outlined'}
                        onClick={() => setShowCustom((s) => !s)}
                        sx={{ fontSize: 11, px: 1.2 }}
                    >
                        Personalizado
                    </Button>
                </Stack>

                {/* Panel de fechas personalizado */}
                <Collapse in={showCustom}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ sm: 'center' }}
                        spacing={1}
                        sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                    >
                        <DatePicker
                            label="Desde"
                            value={desdeTmp}
                            onChange={(v) => v && setDesdeTmp(v)}
                            format="DD/MM/YYYY"
                            slotProps={{
                                textField: { size: 'small', sx: { width: 160 } },
                            }}
                        />
                        <Typography variant="caption" color="text.disabled">→</Typography>
                        <DatePicker
                            label="Hasta"
                            value={hastaTmp}
                            onChange={(v) => v && setHastaTmp(v)}
                            format="DD/MM/YYYY"
                            minDate={desdeTmp}
                            slotProps={{
                                textField: { size: 'small', sx: { width: 160 } },
                            }}
                        />
                        <Button
                            variant="contained"
                            size="small"
                            onClick={aplicarCustom}
                            disabled={!desdeTmp || !hastaTmp || desdeTmp.isAfter(hastaTmp)}
                            sx={{ fontSize: 11, px: 2, whiteSpace: 'nowrap' }}
                        >
                            Aplicar
                        </Button>
                    </Stack>
                </Collapse>

                {/* Rango activo */}
                {value?.from && (
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9 }}>
                        {fmtDisplay(value.from)} → {fmtDisplay(value.to)}
                    </Typography>
                )}
            </Stack>
        </LocalizationProvider>
    );
}